import { useAuthContext } from '@/auth/useAuthContext';
import { ApiError, approveRefundRequest, listRefundRequests, rejectRefundRequest } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { ApproveRefundRequestArgs, EnrichedRefundRequest, RefundStatus } from '@/lib/types';
import { RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

/** Label + colour for each refund status. */
function statusMeta(status: RefundStatus): { label: string; className: string } {
  switch (status) {
    case 'pending':
      return {
        label: 'Pending',
        className: 'bg-warning/10 text-warning ring-hairline',
      };
    case 'approved':
      return {
        label: 'Approved',
        className: 'bg-primary/10 text-primary ring-hairline',
      };
    case 'rejected':
      return {
        label: 'Rejected',
        className: 'bg-destructive/10 text-destructive ring-hairline',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        className: 'bg-secondary text-muted-foreground ring-hairline',
      };
    case 'completed':
      return {
        label: 'Completed',
        className: 'bg-success/10 text-success ring-hairline',
      };
    case 'failed':
      return {
        label: 'Failed',
        className: 'bg-destructive/10 text-destructive ring-hairline',
      };
  }
}

function StatusBadge({ status }: { status: RefundStatus }) {
  const { label, className } = statusMeta(status);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium leading-none ${className}`}
    >
      {label}
    </span>
  );
}

function bookingSummary(booking: { id: string; checkIn: string; checkout: string } | null): string {
  if (!booking) return '—';
  const checkIn = booking.checkIn.slice(0, 10);
  const checkout = booking.checkout.slice(0, 10);
  return `${checkIn} → ${checkout}`;
}

// ---------------------------------------------------------------------------
// Status filter
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: Array<{ label: string; value: RefundStatus | 'all' }> = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
  { label: 'All', value: 'all' },
];

interface StatusFilterProps {
  active: RefundStatus | 'all';
  onChange: (v: RefundStatus | 'all') => void;
}

function StatusFilter({ active, onChange }: StatusFilterProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {STATUS_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors press ${
            active === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReviewDrawer
// ---------------------------------------------------------------------------

interface ReviewDrawerProps {
  request: EnrichedRefundRequest;
  refundDefault: 'card' | 'credit' | string;
  onApprove: (args: ApproveRefundRequestArgs) => Promise<void>;
  onReject: (staffNote: string) => Promise<void>;
  onClose: () => void;
}

function ReviewDrawer({ request, refundDefault, onApprove, onReject, onClose }: ReviewDrawerProps) {
  const currency = request.original_payment.currency;
  const origSource = request.original_payment.source;

  // Determine default source: if account.refund_default === 'credit' and original was Stripe,
  // default to 'credit'. Otherwise, no override (empty = use original source).
  const defaultSourceOverride: '' | 'cash' | 'check' | 'credit' = (() => {
    if (
      refundDefault === 'credit' &&
      (origSource === 'stripe_card' || origSource === 'stripe_terminal')
    ) {
      return 'credit';
    }
    return '';
  })();

  const [approvedAmountText, setApprovedAmountText] = useState(() =>
    (request.requestedAmountMinor / 100).toFixed(2),
  );
  const [sourceOverride, setSourceOverride] = useState<'' | 'cash' | 'check' | 'credit'>(
    defaultSourceOverride,
  );
  const [staffNote, setStaffNote] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customerName = request.customer
    ? `${request.customer.firstName} ${request.customer.lastName}`
    : '—';

  async function handleApprove() {
    const parsed = Number(approvedAmountText.trim());
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Approved amount must be a positive number');
      return;
    }
    const approvedMinor = Math.round(parsed * 100);
    if (!Number.isInteger(approvedMinor) || approvedMinor <= 0) {
      setError('Approved amount must be a positive number');
      return;
    }
    if (sourceOverride === 'check' && chequeNo.trim() === '') {
      setError('Cheque number is required for cheque refunds');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const args: ApproveRefundRequestArgs = {
        approved_amount_minor: approvedMinor,
        ...(sourceOverride !== '' ? { source_override: sourceOverride } : {}),
        ...(staffNote.trim() !== '' ? { staff_note: staffNote.trim() } : {}),
        ...(sourceOverride === 'check' && chequeNo.trim() !== ''
          ? { cheque_no: chequeNo.trim() }
          : {}),
      };
      await onApprove(args);
    } catch (err) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
      setBusy(false);
    }
  }

  async function handleReject() {
    setBusy(true);
    setError(null);
    try {
      await onReject(staffNote.trim() || '');
    } catch (err) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
      setBusy(false);
    }
  }

  const approvedAmountMinor = Math.round(Number(approvedAmountText) * 100);
  const approveLabel =
    Number.isFinite(approvedAmountMinor) && approvedAmountMinor > 0
      ? `Approve ${formatPriceMinor(approvedAmountMinor, currency)}`
      : 'Approve';

  return (
    <dialog
      className="fixed inset-0 z-50 flex justify-end bg-foreground/40 p-0"
      aria-label="Review refund request"
      open
    >
      <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-surface p-6 ring-1 ring-border elevate">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">Review refund request</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Context block */}
        <div className="mb-5 space-y-3 rounded-xl bg-secondary/60 p-4 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Customer</span>
            <span className="font-medium">{customerName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Booking</span>
            <span className="font-medium">{bookingSummary(request.booking)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Original payment</span>
            <span className="num font-medium">
              {formatPriceMinor(request.original_payment.amountMinor, currency)} · {origSource}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Customer requested</span>
            <span className="num font-medium">
              {formatPriceMinor(request.requestedAmountMinor, currency)}
            </span>
          </div>
          <div className="border-t border-border pt-3">
            <p className="mb-1 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Reason
            </p>
            <p className="text-sm">{request.reason}</p>
          </div>
        </div>

        {/* Approve form */}
        <div className="space-y-4">
          <div>
            <label
              htmlFor="rr-approved-amount"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Approved amount ({currency})
            </label>
            <input
              id="rr-approved-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={approvedAmountText}
              onChange={(e) => setApprovedAmountText(e.target.value)}
              className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div>
            <label
              htmlFor="rr-source-override"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Refund via (optional override)
            </label>
            <select
              id="rr-source-override"
              value={sourceOverride}
              onChange={(e) =>
                setSourceOverride(e.target.value as '' | 'cash' | 'check' | 'credit')
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="">Default (same as original)</option>
              <option value="cash">Cash</option>
              <option value="check">Cheque</option>
              <option value="credit">Account credit</option>
            </select>
          </div>

          {sourceOverride === 'check' && (
            <div>
              <label
                htmlFor="rr-cheque-no"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Cheque number
              </label>
              <input
                id="rr-cheque-no"
                type="text"
                value={chequeNo}
                onChange={(e) => setChequeNo(e.target.value)}
                placeholder="e.g. 001234"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          )}

          <div>
            <label
              htmlFor="rr-staff-note"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Staff note (optional)
            </label>
            <textarea
              id="rr-staff-note"
              rows={3}
              value={staffNote}
              onChange={(e) => setStaffNote(e.target.value)}
              placeholder="Internal note visible to staff only"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {error !== null && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <div className="mt-6 flex justify-between gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleReject()}
            className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/5 press disabled:opacity-50"
          >
            Reject
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleApprove()}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {busy ? 'Processing…' : approveLabel}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RefundRequestsPage() {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const refundDefault = account?.refundDefault ?? 'card';

  const [statusFilter, setStatusFilter] = useState<RefundStatus | 'all'>('pending');
  const [requests, setRequests] = useState<EnrichedRefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [reviewing, setReviewing] = useState<EnrichedRefundRequest | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const statusArg = statusFilter === 'all' ? undefined : statusFilter;
      const res = await listRefundRequests(statusArg);
      setRequests(res.refund_requests);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function closeDrawer() {
    setReviewing(null);
    setActionError(null);
  }

  if (permissionDenied) {
    return (
      <div className="mx-auto max-w-[1100px] px-6 py-10 md:px-10 md:py-14">
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <p className="text-sm font-medium text-foreground">Insufficient permissions</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Ask an owner or manager to grant access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-10 md:px-10 md:py-14">
      <header className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <RotateCcw className="size-5 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Payments
          </p>
        </div>
        <h1 className="font-heading text-3xl font-medium tracking-tight">Refund requests</h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Review and process customer refund requests. Approved refunds are applied immediately.
        </p>
      </header>

      <div className="mb-6">
        <StatusFilter active={statusFilter} onChange={setStatusFilter} />
      </div>

      {actionError !== null && (
        <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {actionError}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <RotateCcw className="mx-auto mb-3 size-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium text-foreground">
            No refund requests with status "{statusFilter}".
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Refund requests appear here when customers request a refund or when staff initiate one.
          </p>
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Customer
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Booking
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Original payment
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Requested
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Reason
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Status
                  </th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requests.map((r) => {
                  const currency = r.original_payment.currency;
                  const customerName = r.customer
                    ? `${r.customer.firstName} ${r.customer.lastName}`
                    : '—';
                  return (
                    <tr key={r.id} className="hover:bg-secondary/30">
                      <td className="px-5 py-3.5 font-medium">{customerName}</td>
                      <td className="px-5 py-3.5 text-muted-foreground">
                        {bookingSummary(r.booking)}
                      </td>
                      <td className="num px-5 py-3.5 text-muted-foreground">
                        {formatPriceMinor(r.original_payment.amountMinor, currency)} ·{' '}
                        {r.original_payment.source}
                      </td>
                      <td className="num px-5 py-3.5">
                        {formatPriceMinor(r.requestedAmountMinor, currency)}
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground">
                        {truncate(r.reason, 80)}
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-5 py-3.5">
                        {r.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => {
                              setActionError(null);
                              setReviewing(r);
                            }}
                            className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-secondary press"
                          >
                            Review
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {reviewing !== null && (
        <ReviewDrawer
          request={reviewing}
          refundDefault={refundDefault}
          onApprove={async (args) => {
            await approveRefundRequest(reviewing.id, args);
            closeDrawer();
            void refresh();
          }}
          onReject={async (note) => {
            await rejectRefundRequest(reviewing.id, note || undefined);
            closeDrawer();
            void refresh();
          }}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}
